import debounce from 'lodash/debounce';
import React, { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { root } from '~/preloaded';
import { TREASURY_TABS } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import useMount from '~/hooks/useMount';
import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import { supplyViewSelector } from '~/store/selectors/supplySelectors';

import { actionsSupply, setSelectedBuildingThunk } from '~/Actions/ActionSupply';

import { IsFromSearchContext } from '~/Providers/IsFromSearch';
import { Back, ClansInformerButton } from '~/UIKit';

import Footer from './Footer/Footer';
import Header from './Header/Header';
import SideBar from './Sidebar/Sidebar';
import SidebarToggleButton from './SidebarToggleButton/SidebarToggleButton';
import SupplyMap from './SupplyMap/SupplyMap';
import styles from './ViewSupply.scss';

import type { BUILDINGS } from '~/Actions/ActionSupply';

const ViewSupply: React.FC<{ clanId: number }> = ({ clanId }) => {
  const {
    battlesStats,
    buildings,
    confirmBuildingId,
    isOwnBase,
    isSidebarOpened,
    mapHost,
    prevSelectedBuildingId,
    selectedBuildingId,
    tempHoverBuildingId,
    tempHoverBuildingLevel,
  } = useAppSelector(supplyViewSelector(clanId));

  const isFromSearch = useContext(IsFromSearchContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const dropSelection = useCallback(() => {
    dispatch(setSelectedBuildingThunk(null));
    dispatch(actionsSupply.hideBuildingConfirm());
  }, [dispatch]);

  const navigateToTreasury = useCallback(
    (tab: TREASURY_TABS = TREASURY_TABS.REWARDS) => {
      navigate(`${root}${ROUTES_MAP.TREASURY}/${tab}`);
    },
    [navigate],
  );

  useMount(() => {
    if (isOwnBase && !isFromSearch) {
      debounce(() => {
        dwhExport.push(DWH_EVENTS.SUPPLY.OPEN_PAGE);
      }, 1000);
    }
  });

  const buildingData = useMemo(() => {
    if (prevSelectedBuildingId && buildings?.[prevSelectedBuildingId]) {
      return { ...buildings[prevSelectedBuildingId] };
    }
    return null;
  }, [prevSelectedBuildingId, buildings]);

  const { title, name, bonuses } = useMemo(() => {
    let title = '';
    let name: Nullable<BUILDINGS> = null;
    let bonuses: string[] = [];

    if (!buildingData) {
      return { title, name, bonuses };
    }

    let level = buildingData.level;

    if (confirmBuildingId) {
      level += 1;
    }

    if (tempHoverBuildingLevel) {
      level = tempHoverBuildingLevel.level;
    }

    title = buildingData.levels[level]?.title || '';
    name = buildingData.name || null;

    if (level === 0) {
      bonuses = [buildingData.levels[0]?.meta?.function || ''];
    } else {
      if (name === 'superships_home') {
        bonuses = [];
        for (let i = 1; i <= level; i++) {
          bonuses.push(buildingData.levels[i]?.meta?.totalBonus || '');
        }
      } else {
        bonuses = [buildingData.levels[level]?.meta?.totalBonus || ''];
      }
    }

    return { title, name, bonuses };
  }, [buildingData, confirmBuildingId, tempHoverBuildingLevel]);

  return (
    <div className={styles.container}>
      <SupplyMap clanId={clanId} />

      <SidebarToggleButton isSidebarOpened={isSidebarOpened} />

      <div className={styles.innerShadow} />

      <Header
        name={name}
        title={title}
        level={buildingData?.level || 0}
        maxLevel={buildingData?.maxLevel || 0}
        selectedBuildingId={selectedBuildingId}
        dropSelection={dropSelection}
      />

      <Footer
        bonuses={bonuses}
        isOwnBase={isOwnBase}
        level={buildingData?.level ?? null}
        confirmBuildingId={confirmBuildingId}
        selectedBuildingId={selectedBuildingId}
        tempHoverBuildingId={tempHoverBuildingId}
        dropSelection={dropSelection}
        navigateToTreasury={navigateToTreasury}
      />

      {buildings && (
        <SideBar
          battlesStats={battlesStats}
          buildings={buildings}
          confirmBuildingId={confirmBuildingId}
          isOwnBase={isOwnBase}
          isSidebarOpened={isSidebarOpened}
          mapHost={mapHost}
          selectedBuildingId={selectedBuildingId}
          tempHoverBuildingLevel={tempHoverBuildingLevel}
          navigateToTreasury={navigateToTreasury}
        />
      )}

      {clanId && (
        <ClansInformerButton
          clanId={clanId}
          source={new URLSearchParams(window.location.search).get('source') || 'profile'}
        />
      )}

      {(!isOwnBase || isFromSearch) && (
        <div className={styles.back}>
          <Back />
        </div>
      )}
    </div>
  );
};

export default ViewSupply;
